import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import useTasks from "@/features/task/hooks/useTasks";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import TaskTableFilters from "@/features/task/components/TaskTableFilters";
import Modal from "@/features/shared/components/Modal/Modal";
import TaskForm from "@/features/task/components/taskForm/TaskForm";
import KanbanColumn from "@/features/shared/components/KanbanColumn/KanbanColumn";
import useTaskStates from "@/features/taskState/hooks/useTaskStates";
import { FiltersSection } from "@/features/task/components/FiltersSection";
import { useAuth } from "@/auth/useAuth";
import { buildDefaultTaskFilters, getRestrictedAssigneeId } from "@/features/task/utils/filterUtils";
const TasksBoardPage = () => {
    const { user } = useAuth();
    const restrictedAssigneeId = useMemo(() => getRestrictedAssigneeId(user), [user]);
    const isRestrictedUser = Boolean(restrictedAssigneeId);
    const [filters, setFilters] = useState(() => buildDefaultTaskFilters(restrictedAssigneeId));
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { taskStates } = useTaskStates(true);
    const { tasks, loading, error, handleAdvanceTask, handleChangeTaskState, handleAddTask } = useTasks(undefined, filters, true);
    useEffect(() => {
        if (!restrictedAssigneeId) {
            return;
        }
        setFilters(prev => {
            const currentAssignees = Array.isArray(prev.assignees) ? prev.assignees : [];
            if (currentAssignees.length === 1 && currentAssignees[0] === restrictedAssigneeId) {
                return prev;
            }
            return {
                ...prev,
                assignees: [restrictedAssigneeId],
            };
        });
    }, [restrictedAssigneeId]);
    // Gérer les changements dans les filtres
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => {
            if (filterName === "assignees") {
                if (isRestrictedUser && restrictedAssigneeId) {
                    return {
                        ...prev,
                        assignees: [restrictedAssigneeId],
                    };
                }
                return {
                    ...prev,
                    assignees: Array.isArray(value) ? value : [],
                };
            }
            const nextFilters = {
                ...prev,
                [filterName]: value,
            };
            if (isRestrictedUser && restrictedAssigneeId) {
                nextFilters.assignees = [restrictedAssigneeId];
            }
            return nextFilters;
        });
    };
    const handleCloseModal = () => setIsModalVisible(false);
    const handleResetFilters = () => {
        setFilters(buildDefaultTaskFilters(restrictedAssigneeId));
    };
    const handleSubmitAddTask = async (taskData) => {
        await handleAddTask(taskData);
        setIsModalVisible(false);
    };
    if (loading) {
        return _jsx("div", { className: "flex justify-center items-center min-h-screen", children: "Chargement..." });
    }
    if (error) {
        return _jsxs("div", { className: "text-red-500 text-center mt-4", children: ["Erreur : ", error] });
    }
    return (_jsx("div", { className: "p-2", children: _jsx(DndProvider, { backend: HTML5Backend, children: _jsx(FiltersSection, { table: _jsxs("div", { className: "bg-gray-100 p-4", children: [_jsx("div", { className: "flex gap-2 min-h-screen overflow-x-auto", children: taskStates.map((state) => (_jsx(KanbanColumn, { taskState: state, tasks: tasks, onAdvanceTask: handleAdvanceTask, loading: loading, onChangeState: handleChangeTaskState, className: `w-1/${taskStates.length} min-w-[16rem] p-2` }, state.id))) }), isModalVisible && (_jsx(Modal, { onClose: () => setIsModalVisible(false), children: _jsx(TaskForm, { taskId: 0, onSubmit: handleSubmitAddTask, onCancel: handleCloseModal }) }))] }), children: _jsx(TaskTableFilters, { filters: filters, isKanban: true, onFilterChange: handleFilterChange, onResetFilters: handleResetFilters }) }) }) }));
};
export default TasksBoardPage;
